/**
 * Sent-message history, stored in the THREAD KV (key `input_history`) — not on
 * the local filesystem. Session state lives server-side as a rule (like
 * approvals and the process registry) so it resumes from any machine and
 * leaves nothing behind on the host.
 *
 * KV is per-thread, but recall should feel per-project: when a NEW session
 * starts, the caller passes the project's newest prior thread as a seed and
 * the history carries over (and is copied to the new thread's KV). Oldest →
 * newest; capped so the value can't grow without bound. All IO is best-effort.
 */

export const HISTORY_KEY = "input_history";
const MAX_ENTRIES = 100;

/** The KV surface history needs (satisfied by ApiClient). */
export interface HistoryStore {
  kvGet(threadId: string, key: string): Promise<unknown>;
  kvSet(threadId: string, key: string, value: unknown): Promise<void>;
}

function clean(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((e): e is string => typeof e === "string" && e.trim() !== "").slice(-MAX_ENTRIES);
}

/**
 * Load the thread's sent-message history. When the thread has none yet (a
 * fresh session) and a seed thread is given, inherit the seed's history and
 * persist it onto this thread so the chain continues from here.
 */
export async function loadHistory(
  store: HistoryStore,
  threadId: string,
  seedThreadId?: string
): Promise<string[]> {
  const own = clean(await store.kvGet(threadId, HISTORY_KEY));
  if (own.length) return own;
  if (seedThreadId && seedThreadId !== threadId) {
    const seeded = clean(await store.kvGet(seedThreadId, HISTORY_KEY));
    if (seeded.length) {
      void store.kvSet(threadId, HISTORY_KEY, seeded);
      return seeded;
    }
  }
  return [];
}

/**
 * Append a sent message to the in-memory history (skipping a consecutive
 * duplicate), cap it, and persist the whole list to the thread KV. Returns
 * the same array, mutated, so the caller's reference stays current.
 */
export function appendHistory(
  store: HistoryStore,
  threadId: string,
  history: string[],
  text: string
): string[] {
  const t = text.trim();
  if (!t || history[history.length - 1] === t) return history;
  history.push(t);
  if (history.length > MAX_ENTRIES) history.splice(0, history.length - MAX_ENTRIES);
  void store.kvSet(threadId, HISTORY_KEY, [...history]);
  return history;
}
